(() => {
  const configElement = document.querySelector('[data-artwork-cart-config]');
  if (!configElement || window.owArtworkCartInstalled) return;
  window.owArtworkCartInstalled = true;
  const config = JSON.parse(configElement.textContent);
  const transport = new window.OutpostArtworkTransport(config.endpoint);
  // Old project links are recognised only so the customer can replace them.
  // No inspection, reservation or mutation is ever sent to a legacy endpoint.
  const legacyTransports = (config.legacyEndpoints || []).filter(endpoint => endpoint !== config.endpoint).map(endpoint => new window.OutpostArtworkTransport(endpoint));
  const controller = new AbortController();
  let checking = false, editing = false, approvedForm = null, checkPromise = null, checkoutPending = false;
  const reference = value => { try { return transport.fileReference(value); } catch (_) { return null; } };
  const cartReference = item => /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(item.properties?._outpost_upload_request || '') ? item.properties._outpost_upload_request : undefined;
  const identity = file => [file.id, file.read_key, file.cart_ref || ''].join(':');
  const properties = item => Object.entries(item.properties || {}).filter(([name,value]) => /^(?:Your design file|_Artwork file)_\d+$/.test(name) && reference(value));
  const legacyProperties = item => Object.entries(item.properties || {}).filter(([name,value]) => /^(?:Your design file|_Artwork file)_\d+$/.test(name) && !reference(value) && legacyTransports.some(legacy => { try { return Boolean(legacy.fileReference(value)); } catch (_) { return false; } }));
  const request = async (url, options = {}) => {
    const response = await fetch(url, {...options, credentials:'same-origin', cache:'no-store', signal:AbortSignal.timeout(20000)});
    if (!response.ok) throw new Error('Basket request failed');
    return response.json();
  };
  const cart = () => request(config.cartUrl + '.js');
  const panels = () => [...document.querySelectorAll('[data-artwork-cart-line]')];
  const forItem = item => panels().filter(panel => panel.dataset.lineKey === item.key);
  const notify = (panel, text, error = false) => {
    const message = panel.querySelector('[data-artwork-cart-message]');
    message.textContent = text;
    message.classList.toggle('ow-cart-artwork-error', error);
    panel.classList.toggle('is-invalid', Boolean(text && error));
    message.hidden = !text;
    panel.hidden = false;
  };
  const show = (item, state) => forItem(item).forEach(panel => {
    panel.dataset.state = state;
    const bad = state !== 'ready';
    panel.querySelector('[data-artwork-cart-actions]').hidden = !bad;
    notify(panel, state === 'reupload' ? config.reupload || config.missing : state === 'missing' ? config.missing : state === 'unavailable' ? config.unavailable : '', bad);
    panel.hidden = !bad;
  });
  const previewLimit = 50 * 1024 * 1024;
  const previewType = /\.(?:png|jpe?g|webp|gif)$/i;
  const previewLoads = new WeakMap(), previewTasks = new Set();
  let previewPageActive = true;
  const clearPreviewLoad = link => {
    const state = previewLoads.get(link);
    if (!state) return;
    clearTimeout(state.timer);
    state.cancel?.();
    previewTasks.delete(state);
    previewLoads.delete(link);
  };
  const loadPreview = (link, url, confirmed = false) => {
    let state = previewLoads.get(link);
    if (state?.key !== url) {
      clearPreviewLoad(link);
      state = {link, key:url, attempts:0, confirmed:false, pending:false};
      previewLoads.set(link, state);
    }
    const existing = link.parentElement?.querySelector('[data-artwork-cart-preview]');
    if (confirmed && !state.confirmed) {
      state.confirmed = true;
      if (state.attempts >= 3 && !state.pending && !existing) state.attempts = 0;
    }
    if (existing?.dataset.previewKey === url && existing.complete && existing.naturalWidth > 0 && existing.naturalHeight > 0) return;
    if (!previewPageActive || state.pending || state.timer || state.attempts >= 3 || !link.isConnected) return;
    existing?.remove();
    const image = document.createElement('img');
    image.className = 'ow-cart-artwork-preview';
    image.dataset.artworkCartPreview = '';
    image.dataset.previewKey = url;
    image.width = 72; image.height = 72;
    image.alt = ''; image.setAttribute('aria-hidden', 'true');
    image.loading = 'eager'; image.decoding = 'async'; image.referrerPolicy = 'no-referrer';
    state.attempts++;
    state.pending = true;
    previewTasks.add(state);
    let settled = false, decoding = false, deadline;
    const cleanup = () => {
      clearTimeout(deadline);
      image.onload = image.onerror = null;
      state.pending = false;
      state.cancel = null;
      previewTasks.delete(state);
    };
    const current = () => previewPageActive && previewLoads.get(link) === state && link.isConnected;
    const failed = () => {
      if (settled) return;
      settled = true;
      cleanup();
      image.removeAttribute('src');
      if (!current() || state.attempts >= 3) return;
      state.timer = setTimeout(() => {
        state.timer = null;
        previewTasks.delete(state);
        if (current()) loadPreview(link, url);
      }, [750, 2000][state.attempts - 1]);
      previewTasks.add(state);
    };
    const paint = () => {
      if (settled) return;
      if (!image.complete || image.naturalWidth <= 0 || image.naturalHeight <= 0) { failed(); return; }
      settled = true;
      cleanup();
      if (!current()) { image.removeAttribute('src'); return; }
      image.dataset.loaded = '';
      link.after(image);
    };
    const loaded = () => {
      if (settled || decoding) return;
      if (!image.complete || image.naturalWidth <= 0 || image.naturalHeight <= 0) { failed(); return; }
      decoding = true;
      if (typeof image.decode === 'function') {
        try { Promise.resolve(image.decode()).then(paint, failed); } catch (_) { failed(); }
      } else paint();
    };
    state.cancel = () => {
      if (settled) return;
      settled = true;
      cleanup();
      image.removeAttribute('src');
    };
    image.onload = loaded;
    image.onerror = failed;
    deadline = setTimeout(failed, 15000);
    image.src = url;
    if (image.complete) loaded();
  };
  window.addEventListener('pagehide', () => {
    previewPageActive = false;
    [...previewTasks].forEach(state => clearPreviewLoad(state.link));
  });
  // The derivative endpoint verifies the read capability itself. Rendering this
  // tiny image must not wait for the separate checkout-availability inspection.
  const renderDomPreviews = (scope = document) => {
    const selector = '[data-native-cart-group] [data-artwork-cart-file]';
    const links = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) links.unshift(scope);
    links.forEach(link => {
      const panel = link.closest('[data-native-cart-group]')?.querySelector('[data-artwork-cart-line]');
      if (['missing', 'reupload'].includes(panel?.dataset.state)) return;
      const canonical = link.dataset.artworkCartFile || link.href;
      const file = reference(canonical);
      if (!file) return;
      try {
        const name = decodeURIComponent(new URL(canonical).pathname.split('/').pop());
        if (!previewType.test(name)) return;
        const url = `${transport.endpoint}/uploads/thumbnail/${file.id}/${file.read_key}/preview.png`;
        if (window.OutpostArtworkPreview?.thumbnailUrl(url, config.endpoint, file)) loadPreview(link, url);
      } catch (_) {}
    });
  };
  const previewRoots = new Map();
  const observePreviewRoot = root => {
    if (previewRoots.has(root)) return;
    const observer = new MutationObserver(records => {
      if (!previewPageActive) return;
      records.forEach(record => record.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE && node.isConnected) renderDomPreviews(node);
      }));
      [...previewTasks].forEach(state => { if (!state.link.isConnected) clearPreviewLoad(state.link); });
    });
    previewRoots.set(root, observer);
    if (previewPageActive) observer.observe(root, {childList:true, subtree:true});
  };
  window.addEventListener('pagehide', () => previewRoots.forEach(observer => observer.disconnect()));
  document.querySelectorAll('cart-drawer, .shopify-section--main-cart').forEach(observePreviewRoot);
  document.addEventListener('shopify:section:load', event => {
    if (event.target.matches?.('cart-drawer, .shopify-section--main-cart')) observePreviewRoot(event.target);
    event.target.querySelectorAll?.('cart-drawer, .shopify-section--main-cart').forEach(observePreviewRoot);
    renderDomPreviews(event.target);
  });
  const previews = (item, metadata) => {
    try { forItem(item).forEach(panel => {
    const line = panel.closest('[data-native-cart-group]');
    line?.querySelectorAll('[data-artwork-cart-file]').forEach(link => {
      const file = reference(link.dataset.artworkCartFile || link.href);
      const row = file && metadata.get(identity({...file, cart_ref:cartReference(item)}));
      const existing = link.parentElement.querySelector('[data-artwork-cart-preview]');
      const usable = row?.state === 'ready' && previewType.test(row.name || '') && Number.isSafeInteger(row.size) && row.size > 0 && row.size <= previewLimit;
      const verified = usable && reference(row.url);
      const previewUrl = verified && window.OutpostArtworkPreview?.thumbnailUrl(row.preview_url, config.endpoint, verified);
      if (!verified || verified.id !== file.id || verified.read_key !== file.read_key || (row.preview_url && !previewUrl)) {
        clearPreviewLoad(link);
        existing?.remove();
        return;
      }
      if (previewUrl) loadPreview(link, previewUrl, true);
    });
    }); } catch (_) {}
  };
  // A small keepalive thumbnail POST can finish just after navigation. Only
  // recent local uploads get bounded metadata retries; originals are never read.
  const previewAttempts = new Map();
  let previewRetryTimer, previewRetryGeneration = 0;
  const retryPendingPreviews = (items, refs, metadata) => {
    clearTimeout(previewRetryTimer);
    const generation = ++previewRetryGeneration;
    const helper = window.OutpostArtworkPreview;
    const pending = refs.filter(file => {
      const row = metadata.get(identity(file));
      if (row?.preview_url) { helper?.clearPending?.(file.id); return false; }
      return row?.state === 'ready' && helper?.pending?.(file.id) && (previewAttempts.get(file.id) || 0) < 3;
    });
    if (!pending.length || document.hidden || !previewPageActive) return;
    const attempt = Math.min(...pending.map(file => previewAttempts.get(file.id) || 0));
    previewRetryTimer = setTimeout(async () => {
      if (document.hidden || generation !== previewRetryGeneration) return;
      const activePending = pending.filter(file => helper?.pending?.(file.id) && (previewAttempts.get(file.id) || 0) < 3);
      for (let offset = 0; offset < activePending.length; offset += 10) {
        if (document.hidden || generation !== previewRetryGeneration) return;
        const batch = activePending.slice(offset, offset + 10);
        new Set(batch.map(file => file.id)).forEach(id => previewAttempts.set(id, (previewAttempts.get(id) || 0) + 1));
        try {
          const result = await transport.control({action:'inspect', files:batch}, controller.signal);
          if (Array.isArray(result.files) && result.files.length === batch.length) batch.forEach((file, index) => {
            const row = result.files[index];
            if (row?.id === file.id && ['ready', 'missing'].includes(row.state)) metadata.set(identity(file), row);
          });
        } catch (_) { /* Optional previews never change checkout validation. */ }
      }
      if (document.hidden || generation !== previewRetryGeneration) return;
      items.forEach(item => previews(item, metadata));
      retryPendingPreviews(items, refs, metadata);
    }, [2000, 5000, 10000][attempt]);
  };
  const stopPreviewRetries = () => { previewRetryGeneration++; clearTimeout(previewRetryTimer); };
  document.addEventListener('visibilitychange', () => { if (document.hidden) stopPreviewRetries(); });
  window.addEventListener('pagehide', stopPreviewRetries);
  const inspect = async () => {
    const current = await cart();
    const relevant = current.items.filter(item => (properties(item).length || legacyProperties(item).length) && item.properties?.['Artwork delivery'] !== config.orderValue);
    const refs = new Map();
    relevant.forEach(item => properties(item).forEach(([,value]) => { const f = {...reference(value), cart_ref:cartReference(item)}; refs.set(identity(f), f); }));
    const list = [...refs.values()], states = new Map(), metadata = new Map();
    for (let offset = 0; offset < list.length; offset += 10) {
      const batch = list.slice(offset, offset + 10);
      const result = await transport.control({action:'inspect', files:batch}, controller.signal);
      if (!Array.isArray(result.files) || result.files.length !== batch.length) throw new Error('Artwork check unavailable');
      batch.forEach((f,index) => {
        const row = result.files[index];
        if (row?.id !== f.id) throw new Error('Artwork check unavailable');
        const state = row.state;
        if (!['ready','missing'].includes(state)) throw new Error('Artwork check unavailable');
        states.set(identity(f), state);
        metadata.set(identity(f), row);
      });
    }
    let valid = true;
    relevant.forEach(item => {
      if (legacyProperties(item).length) { show(item, 'reupload'); previews(item, new Map()); valid = false; return; }
      const ready = properties(item).every(([,value]) => { const f = {...reference(value), cart_ref:cartReference(item)}; return states.get(identity(f)) === 'ready'; });
      show(item, ready ? 'ready' : 'missing');
      previews(item, metadata);
      if (!ready) valid = false;
    });
    retryPendingPreviews(relevant, list, metadata);
    panels().filter(panel => !relevant.some(item => item.key === panel.dataset.lineKey)).forEach(panel => { panel.hidden = true; });
    return valid;
  };
  const unavailable = () => panels().forEach(panel => {
    panel.dataset.state = 'unavailable';
    panel.querySelector('[data-artwork-cart-actions]').hidden = false;
    notify(panel, config.unavailable, true);
  });
  const visibleProblem = element => {
    if (!element || element.hidden || element.closest('[hidden], [inert], [aria-hidden="true"]') || !element.getClientRects().length) return false;
    if (element.checkVisibility && !element.checkVisibility({visibilityProperty:true})) return false;
    return !['hidden', 'collapse'].includes(getComputedStyle(element).visibility);
  };
  const focusProblem = () => {
    const panel = panels().find(visibleProblem);
    const message = panel?.querySelector('[data-artwork-cart-message]');
    if (!message) return;
    if (window.OutpostNativeCart?.revealProblem) { window.OutpostNativeCart.revealProblem(panel, message); return; }
    message.focus({preventScroll:true});
    const frame = window.requestAnimationFrame || (callback => setTimeout(callback, 0));
    frame(() => frame(() => {
      if (!panel.isConnected || panel.hidden || !message.isConnected || message.hidden) return;
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportHeight = viewport?.height || window.innerHeight;
      let scroller = panel.parentElement;
      while (scroller && scroller !== document.body && scroller !== document.documentElement) {
        if (/(auto|scroll)/.test(getComputedStyle(scroller).overflowY) && scroller.scrollHeight > scroller.clientHeight) break;
        scroller = scroller.parentElement;
      }
      if (scroller === document.body || scroller === document.documentElement) scroller = null;
      const header = document.querySelector('.shopify-section--header');
      const headerHeight = header && ['sticky', 'fixed'].includes(getComputedStyle(header).position) ? Math.max(0, header.getBoundingClientRect().bottom - viewportTop) : 0;
      const previewBar = document.getElementById('PBarNextFrameWrapper');
      const previewRect = previewBar?.getBoundingClientRect();
      const bottomInset = previewRect?.height && getComputedStyle(previewBar).position === 'fixed' ? Math.max(0, viewportTop + viewportHeight - previewRect.top) : 0;
      const scrollRect = scroller?.getBoundingClientRect();
      const top = scroller ? Math.max(viewportTop, scrollRect.top) + 16 : viewportTop + headerHeight + 16;
      const bottom = scroller ? Math.min(viewportTop + viewportHeight, scrollRect.bottom) - 16 : viewportTop + viewportHeight - bottomInset - 16;
      const available = Math.max(0, bottom - top);
      const rect = panel.getBoundingClientRect();
      const messageRect = message.getBoundingClientRect();
      const destination = rect.height <= available ? top + (available - rect.height) / 2 : top;
      let delta = rect.top - destination;
      if (messageRect.bottom - delta > bottom) delta = messageRect.bottom - bottom;
      if (messageRect.top - delta < top) delta = messageRect.top - top;
      if (Math.abs(delta) > 1) (scroller || window).scrollTo({top:Math.max(0,(scroller ? scroller.scrollTop : window.scrollY) + delta),behavior:window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    }));
  };
  const check = async (focus = false) => {
    if (editing) return false;
    if (!checkPromise) {
      checking = true;
      checkPromise = (async () => {
        try { return await inspect(); }
        catch (_) { unavailable(); return false; }
        finally { checking = false; checkPromise = null; }
      })();
    }
    const valid = await checkPromise;
    if (!valid && focus) focusProblem();
    return valid;
  };
  window.OutpostArtworkCart = Object.freeze({check});
  document.addEventListener('submit', async event => {
    if (window.OutpostNativeCart) return;
    const form = event.target;
    const isCheckout = event.submitter?.name === 'checkout';
    if (!isCheckout || !panels().length || approvedForm === form) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (editing || checkoutPending) return;
    const button = event.submitter;
    checkoutPending = true;
    button.setAttribute('aria-busy','true');
    try {
      const valid = await check(true);
      if (valid && button.isConnected) {
        approvedForm = form;
        try { form.requestSubmit(button); } finally { approvedForm = null; }
      }
    } finally { checkoutPending = false; button.removeAttribute('aria-busy'); }
  }, true);
  const update = async (item, values) => {
    const latest = await cart();
    const same = latest.items.find(row => row.key === item.key);
    if (!same) throw new Error('Basket changed');
    window.OutpostArtworkTransport.syncBundleArtwork(values);
    const response = await request(config.changeUrl, {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({id:same.key, quantity:same.quantity, properties:values})});
    const marker = values._outpost_upload_request;
    if (!response.items?.some(row => row.properties?._outpost_upload_request === marker && (values['Artwork delivery'] ? row.properties?.['Artwork delivery'] === values['Artwork delivery'] : properties(row).map(x=>x[1]).join('|') === properties({properties:values}).map(x=>x[1]).join('|')))) throw new Error('Basket update unconfirmed');
    location.assign(config.cartUrl);
  };
  document.addEventListener('click', async event => {
    const retry = event.target.closest('[data-artwork-cart-retry]');
    if (retry) { event.preventDefault(); await check(true); return; }
    const email = event.target.closest('[data-artwork-cart-email]');
    if (!email || editing || checking) return;
    event.preventDefault();
    const panel = email.closest('[data-artwork-cart-line]');
    editing = true; panel.setAttribute('aria-busy','true');
    try {
      const current = await cart(), item = current.items.find(row => row.key === panel.dataset.lineKey);
      if (!item) throw new Error('Basket changed');
      const values = {...item.properties};
      Object.keys(values).filter(name => /^Your design file_\d+$|^_Artwork (file|original filename|SHA256)_\d+$/.test(name)).forEach(name => delete values[name]);
      values['Artwork delivery'] = config.orderValue;
      values._outpost_upload_request ||= crypto.randomUUID();
      await update(item, values);
    } catch (_) { notify(panel, config.updateFailed, true); }
    finally { editing = false; panel.removeAttribute('aria-busy'); }
  });
  document.addEventListener('change', async event => {
    if (!event.target.matches('[data-artwork-cart-replace]') || editing || checking) return;
    const input = event.target, panel = input.closest('[data-artwork-cart-line]'), files = [...input.files];
    input.value = '';
    if (!files.length) return;
    if (files.length > Number(panel.dataset.maxFiles) || files.some(file => !file.size || file.size > 52428800 || !/\.(ai|eps|pdf|svg|png|jpe?g|emf|webp|gif|tiff?|heic|heif|avif|bmp|psd|cdr|dxf)$/i.test(file.name))) { notify(panel, config.invalidFiles, true); return; }
    editing = true; panel.setAttribute('aria-busy','true');
    try {
      const current = await cart(), item = current.items.find(row => row.key === panel.dataset.lineKey);
      if (!item) throw new Error('Basket changed');
      const results = [];
      for (const file of files) {
        const job = {file, key:[...crypto.getRandomValues(new Uint8Array(32))].map(v=>v.toString(16).padStart(2,'0')).join('')};
        const upload = transport.upload(job, percentage => notify(panel, config.uploading.replace('{percent}', percentage)), controller.signal);
        if (window.OutpostArtworkPreview?.eligible(file) && typeof transport.savePreview === 'function') {
          window.OutpostArtworkPreview.create(file, {signal:controller.signal}).then(async preview => {
            let id;
            try {
              const result = await upload;
              id = reference(result.url)?.id;
              window.OutpostArtworkPreview.markPending?.(id);
              await transport.savePreview(result, job.key, preview.blob);
            } finally { preview.dispose(); }
          }).catch(() => {});
        }
        results.push(await upload);
      }
      const values = {...item.properties};
      const privateFiles = Object.keys(values).some(name => /^_Artwork file_\d+$/.test(name));
      Object.keys(values).filter(name => /^Your design file_\d+$|^_Artwork (file|original filename|SHA256)_\d+$|^Artwork delivery$/.test(name)).forEach(name => delete values[name]);
      // Replacement is a new reservation even if this line came from an old order.
      // Shopify grouping is separate and remains unchanged.
      values._outpost_upload_request = crypto.randomUUID();
      await transport.prepareCart(results.map(result => result.url), values._outpost_upload_request, controller.signal);
      results.forEach((result,index) => {
        values['Your design file_' + (index + 1)] = privateFiles ? result.name : result.url;
        if (privateFiles) values['_Artwork file_' + (index + 1)] = result.url;
      });
      await update(item, values);
    } catch (_) { notify(panel, config.updateFailed, true); }
    finally { editing = false; panel.removeAttribute('aria-busy'); }
  });
  let scheduled;
  const schedule = () => { clearTimeout(scheduled); if (previewPageActive) scheduled = setTimeout(() => { if (panels().length) check(); }, 100); };
  window.addEventListener('pagehide', () => clearTimeout(scheduled));
  const changed = () => { renderDomPreviews(); schedule(); };
  document.addEventListener('cart:change', changed);
  document.addEventListener('cart:refresh', changed);
  document.addEventListener('dialog:after-show', event => {
    if (!event.target.matches('cart-drawer')) return;
    renderDomPreviews(event.target);
  });
  window.addEventListener('ow:artwork-preview-ready', changed);
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    previewPageActive = true;
    previewRoots.forEach((observer, root) => {
      if (root.isConnected) observer.observe(root, {childList:true, subtree:true});
      else previewRoots.delete(root);
    });
    document.querySelectorAll('cart-drawer, .shopify-section--main-cart').forEach(observePreviewRoot);
    changed();
  });
  renderDomPreviews(); schedule();
})();

